import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import { delayForIndexing } from "@examspark/common";
import {
  Breadcrumbs,
  useModal,
  useMutationArchiveExam,
} from "@examspark/common-app";
import {
  Button,
  CautionBanner,
  ConfirmModal,
  RouterLink,
  toast,
} from "@examspark/common-ui";
import { IconTrashLine } from "@examspark/common-ui/dist/components/icons";

import ExamAddToClassroomModal from "../../components/activity/ExamAddToClassroomModal";
import ExamDetail from "../../components/activity/ExamDetail";
import VisibilityModal from "../../components/resources/VisibilityModal";
import { getVisibilityDescription } from "../../utils";

const ExamDetailRoute = () => {
  const navigate = useNavigate();
  const client = useQueryClient();
  const params = useParams<{
    id: string;
  }>();
  const visibilityModal = useModal();

  const archiveAssessment = useMutationArchiveExam();

  if (params.id == null) return null;

  return (
    <>
      <Breadcrumbs
        items={[
          ...Breadcrumbs.Dashboard(),
          <RouterLink key="assessments" to="/workspace/exams">
            Assessments
          </RouterLink>,
        ]}
      />

      <ExamDetail
        examId={params.id}
        assessmentCenter
        toolbar={({ exam, canSeeEdit }) => {
          const { IconComponent: VisibilityIcon } = getVisibilityDescription(
            exam.visibility ?? undefined,
          );

          return (
            <>
              <VisibilityModal
                resource={exam}
                {...visibilityModal.modalProps}
              />
              {canSeeEdit && (
                <>
                  <Button
                    title="Share Assessment"
                    variant="plain"
                    onClick={visibilityModal.open}
                  >
                    <VisibilityIcon width="16px" />
                  </Button>
                  <Button
                    title="Delete Assessment"
                    variant="plain"
                    onClick={async () => {
                      await ConfirmModal.show({
                        onResolving: async (result) => {
                          if (!result) return;
                          try {
                            await archiveAssessment.mutateAsync({
                              id: exam.id,
                              sk: exam.sk,
                            });
                            await delayForIndexing();
                            toast.success("Assessment deleted");
                            navigate("/workspace/exams");
                            client.invalidateQueries(["browse"]);
                          } catch (e) {
                            console.error(e);
                            toast.error("Failed to delete assessment");
                          }
                        },
                        title: "Delete Assessment",
                        confirmColor: "danger",
                        confirmLabel: "Delete",
                        children: (
                          <div style={{ padding: "1rem" }}>
                            <CautionBanner variant="plain">
                              Are you sure you wish to delete "{exam.name}
                              "? Deleted assessments are NOT removed from
                              classrooms where they are already in use.
                            </CautionBanner>
                          </div>
                        ),
                      });
                    }}
                  >
                    <IconTrashLine width="16px" />
                  </Button>
                </>
              )}
              <ExamAddToClassroomModal exam={exam} />
            </>
          );
        }}
      />
    </>
  );
};

export default ExamDetailRoute;
